<template>
  <div class="issuer-settings-page">
    <header class="issuer-hero">
      <div class="hero-main">
        <v-btn icon class="touch-target back-button" aria-label="Zur Dokumentenablage zurück" @click="goBack">
          <v-icon color="white">mdi-arrow-left</v-icon>
        </v-btn>
        <div class="hero-icon" aria-hidden="true">
          <v-icon color="#65c98f">mdi-domain</v-icon>
        </div>
        <div class="hero-copy">
          <div class="hero-eyebrow">Dokumentenablage</div>
          <h1>Rechnungssteller</h1>
          <p>Belegzuordnungen und Kategorie-Regeln zentral verwalten.</p>
        </div>
      </div>

      <div v-if="!loading" class="hero-summary">
        <strong>{{ issuers.length }}</strong>
        <span>Rechnungssteller</span>
      </div>
    </header>

    <section class="settings-shell">
      <div class="settings-toolbar">
        <div>
          <div class="toolbar-eyebrow">Belege & Regeln</div>
          <h2>Rechnungssteller verwalten</h2>
          <p>Belegzuordnungen korrigieren, ungenutzte Rechnungssteller entfernen und Kategorien festlegen.</p>
        </div>

        <div class="toolbar-controls">
          <v-text-field
            v-model="search"
            dark
            outlined
            dense
            clearable
            hide-details
            prepend-inner-icon="mdi-magnify"
            label="Rechnungssteller suchen"
            class="search-field"></v-text-field>

          <v-switch
            v-model="showUnconfiguredOnly"
            dark
            inset
            hide-details
            color="#65c98f"
            label="Nur nicht konfigurierte"
            class="filter-switch"></v-switch>

          <v-switch
            v-model="showUnusedOnly"
            dark
            inset
            hide-details
            color="#65c98f"
            label="Nur ohne Belege"
            class="filter-switch"></v-switch>
        </div>
      </div>

      <v-progress-linear v-if="loading" indeterminate color="#009640" height="3"></v-progress-linear>

      <v-alert v-if="loadError" type="error" text class="settings-alert">
        {{ loadError }}
        <v-btn text color="error" class="retry-button" @click="fetchInvoiceIssuers">Erneut laden</v-btn>
      </v-alert>

      <v-alert v-if="usageError" type="warning" text class="settings-alert">
        {{ usageError }}
        <v-btn text class="retry-button" :loading="usageLoading" @click="refreshUsage">Anzahlen aktualisieren</v-btn>
      </v-alert>

      <div v-if="!loading && !loadError && filteredIssuers.length === 0" class="empty-state">
        <div class="empty-icon"><v-icon color="#65c98f">mdi-domain-off</v-icon></div>
        <h3>Keine Rechnungssteller gefunden</h3>
        <p v-if="search || showUnconfiguredOnly || showUnusedOnly">Keine Treffer für diese Auswahl. Passe die Suche oder die Filter an.</p>
        <p v-else>Noch sind keine Rechnungssteller vorhanden.</p>
      </div>

      <v-expansion-panels v-else-if="!loadError" v-model="expanded" multiple flat class="issuer-panels">
        <v-expansion-panel v-for="issuer in filteredIssuers" :key="issuer._id" class="issuer-panel">
          <v-expansion-panel-header class="issuer-panel-header">
            <div class="issuer-heading">
              <div class="issuer-avatar" aria-hidden="true">{{ getInitials(issuer.title) }}</div>
              <div class="issuer-title-wrap">
                <strong>{{ issuer.title }}</strong>
                <span class="issuer-usage">{{ getDocumentCountLabel(issuer.documentCount) }}</span>
                <span>{{ getIssuerSummary(issuer) }}</span>
              </div>
              <v-chip small outlined :color="isIssuerConfigured(issuer) ? '#65c98f' : '#a3a3a3'" class="status-chip">
                {{ isIssuerConfigured(issuer) ? "Konfiguriert" : "Offen" }}
              </v-chip>
            </div>
          </v-expansion-panel-header>

          <v-expansion-panel-content>
            <div class="issuer-settings">
              <div class="issuer-document-actions">
                <div>
                  <strong>{{ getDocumentCountLabel(issuer.documentCount) }}</strong>
                  <p>Über alle Studios, Ordner und Bearbeitungsstände hinweg.</p>
                </div>
                <div class="issuer-action-buttons">
                  <v-btn outlined color="#65c98f" class="issuer-action" @click="openDocuments(issuer)">
                    <v-icon left small>mdi-file-document-multiple-outline</v-icon>
                    Belege anzeigen
                  </v-btn>
                  <v-btn
                    v-if="issuer.documentCount === 0"
                    outlined
                    color="#ff9ba5"
                    class="issuer-action"
                    :disabled="issuer.saving || usageLoading || Boolean(usageError)"
                    @click="openDeleteDialog(issuer)">
                    <v-icon left small>mdi-delete-outline</v-icon>
                    Löschen
                  </v-btn>
                </div>
              </div>

              <section class="default-category-section">
                <div class="section-copy">
                  <div class="section-icon"><v-icon small color="#65c98f">mdi-tag-outline</v-icon></div>
                  <div>
                    <h3>Standardkategorie</h3>
                    <p>Wird vorausgewählt, wenn keine Spezialregel zum Rechnungsbetrag passt.</p>
                  </div>
                </div>

                <v-autocomplete
                  v-model="issuer.defaultCategory"
                  :items="categories"
                  dark
                  outlined
                  dense
                  clearable
                  hide-details="auto"
                  label="Standardkategorie"
                  @change="markDirty(issuer)"
                  class="category-field"></v-autocomplete>
              </section>

              <section class="rules-section">
                <div class="rules-heading">
                  <div class="section-copy">
                    <div class="section-icon"><v-icon small color="#65c98f">mdi-call-split</v-icon></div>
                    <div>
                      <h3>Spezialregeln</h3>
                      <p>Von oben nach unten geprüft. Die erste passende Regel bestimmt die Kategorie.</p>
                    </div>
                  </div>

                  <v-btn outlined color="#65c98f" class="add-rule-button" :disabled="issuer.categoryRules.length >= 25" @click="addRule(issuer)">
                    <v-icon left small>mdi-plus</v-icon>
                    Regel hinzufügen
                  </v-btn>
                </div>

                <div v-if="issuer.categoryRules.length === 0" class="rules-empty">
                  <v-icon color="#77797f">mdi-filter-outline</v-icon>
                  <span>Keine Spezialregeln. Es gilt immer die Standardkategorie.</span>
                </div>

                <article v-for="(rule, ruleIndex) in issuer.categoryRules" :key="rule.clientId" class="rule-card">
                  <div class="rule-index">{{ ruleIndex + 1 }}</div>

                  <div class="rule-fields">
                    <v-select
                      v-model="rule.operator"
                      :items="operatorOptions"
                      item-text="text"
                      item-value="value"
                      dark
                      outlined
                      dense
                      hide-details="auto"
                      label="Bedingung"
                      @change="markDirty(issuer)"
                      class="rule-field"></v-select>

                    <v-text-field
                      v-model="rule.amount"
                      dark
                      outlined
                      dense
                      hide-details="auto"
                      type="number"
                      min="0"
                      step="0.01"
                      prefix="CHF"
                      label="Betrag"
                      @input="markDirty(issuer)"
                      class="rule-field"></v-text-field>

                    <v-autocomplete
                      v-model="rule.category"
                      :items="categories"
                      dark
                      outlined
                      dense
                      hide-details="auto"
                      label="Dann Kategorie"
                      @change="markDirty(issuer)"
                      class="rule-field rule-category-field"></v-autocomplete>
                  </div>

                  <div class="rule-actions">
                    <v-btn
                      icon
                      class="touch-target"
                      :disabled="ruleIndex === 0"
                      :aria-label="`Regel ${ruleIndex + 1} nach oben verschieben`"
                      @click="moveRule(issuer, ruleIndex, -1)">
                      <v-icon>mdi-arrow-up</v-icon>
                    </v-btn>
                    <v-btn
                      icon
                      class="touch-target"
                      :disabled="ruleIndex === issuer.categoryRules.length - 1"
                      :aria-label="`Regel ${ruleIndex + 1} nach unten verschieben`"
                      @click="moveRule(issuer, ruleIndex, 1)">
                      <v-icon>mdi-arrow-down</v-icon>
                    </v-btn>
                    <v-btn
                      icon
                      color="error"
                      class="touch-target"
                      :aria-label="`Regel ${ruleIndex + 1} löschen`"
                      @click="removeRule(issuer, ruleIndex)">
                      <v-icon>mdi-delete-outline</v-icon>
                    </v-btn>
                  </div>
                </article>
              </section>

              <v-alert v-if="issuer.error" type="error" text class="issuer-alert">{{ issuer.error }}</v-alert>
              <v-alert v-if="issuer.saved" type="success" text class="issuer-alert">Regeln gespeichert.</v-alert>

              <footer class="issuer-footer">
                <div class="footer-hint">
                  <v-icon small color="#8f9095">mdi-information-outline</v-icon>
                  <span>Die vorgeschlagene Kategorie kann beim Dokument weiterhin manuell geändert werden.</span>
                </div>
                <v-btn color="#009640" depressed class="save-button" :loading="issuer.saving" @click="saveIssuer(issuer)">
                  <v-icon left small>mdi-content-save-outline</v-icon>
                  Speichern
                </v-btn>
              </footer>
            </div>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </section>

    <v-dialog
      v-model="documentsDialog"
      :fullscreen="$vuetify.breakpoint.smAndDown"
      :persistent="savingDocument"
      max-width="1000"
      scrollable>
      <v-card dark class="issuer-documents-dialog">
        <header class="documents-dialog-header">
          <div>
            <div class="toolbar-eyebrow">Zugeordnete Belege</div>
            <h2>{{ activeIssuer ? activeIssuer.title : "Belege" }}</h2>
            <p>{{ getDocumentCountLabel(documentsTotal) }} · Alle Studios und Ordner</p>
          </div>
          <v-btn icon class="touch-target" :disabled="savingDocument" aria-label="Belegliste schliessen" @click="documentsDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </header>
        <v-card-text class="documents-dialog-body">
          <p class="documents-intro">Wähle beim betreffenden Beleg den richtigen Rechnungssteller. Die übrigen Belegdaten und die Originaldatei bleiben unverändert.</p>
          <v-alert v-if="documentsSuccess" type="success" text>{{ documentsSuccess }}</v-alert>
          <v-alert v-if="documentsError" type="error" text>
            {{ documentsError }}
            <v-btn text class="issuer-action" :disabled="savingDocument" @click="reloadDocuments">Neu laden</v-btn>
          </v-alert>
          <div v-if="documentsLoading" class="documents-empty" role="status">
            <v-progress-circular indeterminate color="#65c98f"></v-progress-circular>
            <p>Belege werden geladen …</p>
          </div>
          <div v-else-if="!documentsError && documents.length === 0" class="documents-empty">
            <v-icon color="#65c98f" size="36">mdi-file-document-outline</v-icon>
            <h3>Keine Belege zugeordnet</h3>
            <p>Du kannst diesen Rechnungssteller in der Übersicht löschen.</p>
          </div>
          <div v-else-if="!documentsLoading" class="document-rows">
            <article v-for="document in documents" :key="document._id" class="document-row">
              <div class="document-row-heading">
                <div class="document-details">
                  <h3>{{ document.title || "Beleg ohne Titel" }}</h3>
                  <dl class="document-meta">
                    <div><dt>Rechnungsnummer</dt><dd>{{ document.invoiceNumber || "–" }}</dd></div>
                    <div><dt>Datum</dt><dd>{{ formatDate(document.date) }}</dd></div>
                    <div><dt>Studio</dt><dd>{{ document.studio ? document.studio.name || document.studio.code : "Nicht zugeordnet" }}</dd></div>
                  </dl>
                </div>
                <v-btn
                  v-if="editingDocumentId !== document._id"
                  outlined
                  color="#65c98f"
                  class="issuer-action"
                  :disabled="Boolean(editingDocumentId)"
                  @click="editDocument(document)">
                  <v-icon left small>mdi-pencil-outline</v-icon>
                  Zuordnung ändern
                </v-btn>
              </div>
              <div v-if="editingDocumentId === document._id" class="document-editor">
                <v-autocomplete
                  v-model="targetIssuerId"
                  :items="issuers"
                  item-text="title"
                  item-value="_id"
                  label="Rechnungssteller"
                  no-data-text="Kein Rechnungssteller gefunden"
                  dark
                  outlined
                  hide-details="auto"
                  :disabled="savingDocument"
                  class="document-issuer-field"></v-autocomplete>
                <div class="issuer-action-buttons">
                  <v-btn text class="issuer-action" :disabled="savingDocument" @click="cancelDocumentEdit">Abbrechen</v-btn>
                  <v-btn
                    color="#009640"
                    class="issuer-action"
                    :loading="savingDocument"
                    :disabled="!targetIssuerId || targetIssuerId === document.invoiceIssuer"
                    @click="saveDocumentIssuer(document)">
                    Speichern
                  </v-btn>
                </div>
              </div>
            </article>
          </div>
        </v-card-text>
        <footer class="documents-dialog-footer">
          <v-btn icon class="touch-target" :disabled="documentsPage <= 1 || documentsBusy" aria-label="Vorherige Belege" @click="fetchDocuments(documentsPage - 1)">
            <v-icon>mdi-chevron-left</v-icon>
          </v-btn>
          <span>Seite {{ documentsPage }} von {{ documentsPageCount }}</span>
          <v-btn icon class="touch-target" :disabled="documentsPage >= documentsPageCount || documentsBusy" aria-label="Weitere Belege" @click="fetchDocuments(documentsPage + 1)">
            <v-icon>mdi-chevron-right</v-icon>
          </v-btn>
        </footer>
      </v-card>
    </v-dialog>

    <v-dialog v-model="deleteDialog" :persistent="deletingIssuer" max-width="480">
      <v-card dark class="issuer-delete-dialog">
        <h2>Rechnungssteller löschen?</h2>
        <p>«{{ deleteIssuer ? deleteIssuer.title : "" }}» und seine Kategorie-Regeln werden endgültig gelöscht. Belege werden dabei nicht gelöscht.</p>
        <p>Das ist nur möglich, wenn weiterhin kein Beleg zugeordnet ist.</p>
        <v-alert v-if="deleteError" type="error" text>{{ deleteError }}</v-alert>
        <div class="issuer-action-buttons">
          <v-btn text class="issuer-action" :disabled="deletingIssuer" @click="deleteDialog = false">Abbrechen</v-btn>
          <v-btn
            color="error"
            class="issuer-action"
            :loading="deletingIssuer"
            :disabled="!deleteIssuer || deleteIssuer.documentCount !== 0 || usageLoading || Boolean(usageError)"
            @click="confirmDeleteIssuer">Endgültig löschen</v-btn>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import api from "@/api"
import documentCategories from "@/constants/documentCategories"
import userService from "@/services/userService"

export default {
  name: "InvoiceIssuerSettings",
  data() {
    return {
      userService,
      categories: documentCategories,
      operatorOptions: [
        { text: "Betrag ist grösser als", value: "gt" },
        { text: "Betrag ist mindestens", value: "gte" },
        { text: "Betrag ist kleiner als", value: "lt" },
        { text: "Betrag ist höchstens", value: "lte" },
        { text: "Betrag ist genau", value: "eq" },
      ],
      issuers: [],
      expanded: [],
      search: "",
      showUnconfiguredOnly: false,
      showUnusedOnly: false,
      loading: false,
      loadError: "",
      nextRuleId: 1,
      usageLoading: false,
      usageError: "",
      documentsDialog: false,
      activeIssuer: null,
      documents: [],
      documentsTotal: null,
      documentsPage: 1,
      documentsPageSize: 20,
      documentsLoading: false,
      documentsError: "",
      documentsSuccess: "",
      documentsRunId: 0,
      editingDocumentId: null,
      targetIssuerId: null,
      savingDocument: false,
      deleteDialog: false,
      deleteIssuer: null,
      deletingIssuer: false,
      deleteError: "",
    }
  },
  computed: {
    filteredIssuers() {
      const query = (this.search || "").trim().toLocaleLowerCase("de-CH")
      return this.issuers.filter((issuer) => {
        const matchesSearch = !query || (issuer.title || "").toLocaleLowerCase("de-CH").includes(query)
        const matchesStatus = !this.showUnconfiguredOnly || !this.isIssuerConfigured(issuer)
        const matchesUsage = !this.showUnusedOnly || issuer.documentCount === 0
        return matchesSearch && matchesStatus && matchesUsage
      })
    },
    documentsPageCount() {
      return Math.max(1, Math.ceil((this.documentsTotal || 0) / this.documentsPageSize))
    },
    documentsBusy() {
      return this.documentsLoading || this.savingDocument || Boolean(this.editingDocumentId)
    },
  },
  watch: {
    search() {
      this.expanded = []
    },
    showUnconfiguredOnly() {
      this.expanded = []
    },
    showUnusedOnly() {
      this.expanded = []
    },
    documentsDialog(open) {
      if (!open) {
        this.documentsRunId += 1
        this.cancelDocumentEdit()
      }
    },
  },
  created() {
    if (!this.userService.hasRight("d_fetchAllDocs")) {
      this.goBack()
      return
    }
    this.fetchInvoiceIssuers()
  },
  beforeDestroy() {
    this.documentsRunId += 1
  },
  methods: {
    goBack() {
      this.$router.push("/dashboard/documents/")
    },
    getInitials(title) {
      return (title || "?")
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part.charAt(0).toUpperCase())
        .join("")
    },
    getIssuerSummary(issuer) {
      const defaultText = issuer.defaultCategory || "Keine Standardkategorie"
      const ruleCount = issuer.categoryRules.length
      return `${defaultText} · ${ruleCount} ${ruleCount === 1 ? "Spezialregel" : "Spezialregeln"}`
    },
    getDocumentCountLabel(count) {
      if (!Number.isInteger(count) || count < 0) return "Beleganzahl nicht verfügbar"
      return `${count} ${count === 1 ? "Beleg" : "Belege"}`
    },
    formatDate(value) {
      if (!value) return "–"
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return "–"
      return date.toLocaleDateString("de-CH", { day: "2-digit", month: "2-digit", year: "numeric", timeZone: "Europe/Zurich" })
    },
    isIssuerConfigured(issuer) {
      return Boolean(issuer.defaultCategory || issuer.categoryRules.length)
    },
    hydrateRule(rule = {}) {
      return {
        _id: rule._id || null,
        clientId: `category-rule-${this.nextRuleId++}`,
        operator: rule.operator || "gt",
        amount: rule.amount === null || rule.amount === undefined ? null : rule.amount,
        category: rule.category || null,
      }
    },
    hydrateIssuer(issuer) {
      return {
        ...issuer,
        documentCount: Number.isInteger(issuer.documentCount) && issuer.documentCount >= 0 ? issuer.documentCount : null,
        defaultCategory: issuer.defaultCategory || null,
        categoryRules: (issuer.categoryRules || []).map((rule) => this.hydrateRule(rule)),
        saving: false,
        saved: false,
        error: "",
      }
    },
    async fetchInvoiceIssuers() {
      this.loading = true
      this.loadError = ""
      try {
        const issuers = await api.invoiceIssuers.fetch({ includeUsage: true })
        this.issuers = issuers.map((issuer) => this.hydrateIssuer(issuer))
        this.usageError = ""
      } catch (error) {
        this.loadError = this.getApiError(error, "Die Rechnungssteller konnten nicht geladen werden.")
      } finally {
        this.loading = false
      }
    },
    async refreshUsage() {
      this.usageLoading = true
      this.usageError = ""
      try {
        const latest = await api.invoiceIssuers.fetch({ includeUsage: true })
        const existing = new Map(this.issuers.map((issuer) => [issuer._id, issuer]))
        // Keep unsaved category rules while refreshing counts and the available issuers.
        this.issuers = latest.map((issuer) => {
          const current = existing.get(issuer._id)
          if (!current) return this.hydrateIssuer(issuer)
          current.documentCount = Number.isInteger(issuer.documentCount) && issuer.documentCount >= 0 ? issuer.documentCount : null
          return current
        })
        this.expanded = []
        return true
      } catch (error) {
        this.issuers.forEach((issuer) => {
          issuer.documentCount = null
        })
        this.usageError = this.getApiError(error, "Die Beleganzahlen konnten nicht aktualisiert werden. Löschen ist bis zur Aktualisierung gesperrt.")
        return false
      } finally {
        this.usageLoading = false
      }
    },
    openDocuments(issuer) {
      this.activeIssuer = issuer
      this.documents = []
      this.documentsTotal = null
      this.documentsPage = 1
      this.documentsSuccess = ""
      this.cancelDocumentEdit()
      this.documentsDialog = true
      this.fetchDocuments(1)
    },
    async fetchDocuments(page = this.documentsPage) {
      if (!this.activeIssuer) return
      const issuerId = this.activeIssuer._id
      const runId = ++this.documentsRunId
      this.documentsLoading = true
      this.documentsError = ""
      try {
        const result = await api.invoiceIssuers.fetchDocuments(issuerId, page)
        if (runId !== this.documentsRunId) return
        this.documents = result.items
        this.documentsTotal = result.total
        this.documentsPage = result.page
        this.documentsPageSize = result.pageSize
        const issuer = this.issuers.find((entry) => entry._id === issuerId)
        if (issuer && issuer.documentCount !== result.total) {
          issuer.documentCount = result.total
          this.expanded = []
        }
      } catch (error) {
        if (runId !== this.documentsRunId) return
        this.documents = []
        this.documentsError = this.getApiError(error, "Die Belege konnten nicht geladen werden.")
      } finally {
        if (runId === this.documentsRunId) this.documentsLoading = false
      }
    },
    async reloadDocuments() {
      if (this.savingDocument) return
      this.cancelDocumentEdit()
      this.documentsSuccess = ""
      await Promise.all([this.refreshUsage(), this.fetchDocuments()])
    },
    editDocument(document) {
      this.editingDocumentId = document._id
      this.targetIssuerId = document.invoiceIssuer
      this.documentsError = ""
      this.documentsSuccess = ""
    },
    cancelDocumentEdit() {
      this.editingDocumentId = null
      this.targetIssuerId = null
    },
    async saveDocumentIssuer(document) {
      if (this.savingDocument || !this.targetIssuerId || this.targetIssuerId === document.invoiceIssuer) return
      const target = this.issuers.find((issuer) => issuer._id === this.targetIssuerId)
      if (!target) return
      this.savingDocument = true
      this.documentsError = ""
      try {
        await api.invoiceIssuers.assignDocument(this.activeIssuer._id, document._id, target._id)
        this.cancelDocumentEdit()
        this.documentsSuccess = `Der Beleg wurde «${target.title}» zugeordnet.`
        const [usageReady] = await Promise.all([this.refreshUsage(), this.fetchDocuments()])
        if (!usageReady && !this.documentsError) {
          this.documentsError = "Die Zuordnung ist gespeichert. Bitte lade die Übersicht neu, um die Beleganzahlen zu aktualisieren."
        }
      } catch (error) {
        this.documentsError = this.getApiError(error, "Die Zuordnung konnte nicht gespeichert werden.")
      } finally {
        this.savingDocument = false
      }
    },
    openDeleteDialog(issuer) {
      if (issuer.documentCount !== 0 || this.usageLoading || this.usageError) return
      this.deleteIssuer = issuer
      this.deleteError = ""
      this.deleteDialog = true
    },
    async confirmDeleteIssuer() {
      if (this.deletingIssuer || this.usageLoading || this.usageError || !this.deleteIssuer || this.deleteIssuer.documentCount !== 0) return
      this.deletingIssuer = true
      this.deleteError = ""
      try {
        await api.invoiceIssuers.delete(this.deleteIssuer._id)
        this.issuers = this.issuers.filter((issuer) => issuer._id !== this.deleteIssuer._id)
        this.expanded = []
        this.deleteDialog = false
        await this.refreshUsage()
      } catch (error) {
        this.deleteError = this.getApiError(error, "Der Rechnungssteller konnte nicht gelöscht werden.")
        const count = error && error.response && error.response.data && error.response.data.documentCount
        if (Number.isInteger(count)) this.deleteIssuer.documentCount = count
      } finally {
        this.deletingIssuer = false
      }
    },
    addRule(issuer) {
      if (issuer.categoryRules.length >= 25) return
      issuer.categoryRules.push(
        this.hydrateRule({
          operator: "gt",
          amount: null,
          category: issuer.defaultCategory || null,
        }),
      )
      issuer.saved = false
      issuer.error = ""
    },
    markDirty(issuer) {
      issuer.saved = false
      issuer.error = ""
    },
    moveRule(issuer, index, direction) {
      const targetIndex = index + direction
      if (targetIndex < 0 || targetIndex >= issuer.categoryRules.length) return
      const rules = [...issuer.categoryRules]
      const movedRule = rules.splice(index, 1)[0]
      rules.splice(targetIndex, 0, movedRule)
      issuer.categoryRules = rules
      issuer.saved = false
    },
    removeRule(issuer, index) {
      issuer.categoryRules.splice(index, 1)
      issuer.saved = false
      issuer.error = ""
    },
    validateIssuer(issuer) {
      for (let index = 0; index < issuer.categoryRules.length; index += 1) {
        const rule = issuer.categoryRules[index]
        const amount = Number(rule.amount)
        if (!rule.operator || rule.amount === null || rule.amount === "" || !Number.isFinite(amount) || amount < 0) {
          return `Bitte prüfe den Betrag in Regel ${index + 1}.`
        }
        if (!rule.category) return `Bitte wähle eine Kategorie in Regel ${index + 1}.`
      }
      return ""
    },
    async saveIssuer(issuer) {
      const validationError = this.validateIssuer(issuer)
      if (validationError) {
        issuer.error = validationError
        issuer.saved = false
        return
      }

      issuer.saving = true
      issuer.saved = false
      issuer.error = ""
      try {
        const payload = {
          defaultCategory: issuer.defaultCategory || null,
          categoryRules: issuer.categoryRules.map((rule) => ({
            operator: rule.operator,
            amount: Number(rule.amount),
            category: rule.category,
          })),
        }
        const updatedIssuer = await api.invoiceIssuers.updateCategorySettings(issuer._id, payload)
        const issuerIndex = this.issuers.findIndex((entry) => entry._id === issuer._id)
        if (issuerIndex !== -1) {
          const hydratedIssuer = this.hydrateIssuer({ ...updatedIssuer, documentCount: issuer.documentCount })
          hydratedIssuer.saved = true
          this.$set(this.issuers, issuerIndex, hydratedIssuer)
        }
      } catch (error) {
        issuer.error = this.getApiError(error, "Die Regeln konnten nicht gespeichert werden.")
      } finally {
        issuer.saving = false
      }
    },
    getApiError(error, fallback) {
      return error && error.response && error.response.data && error.response.data.error ? error.response.data.error : fallback
    },
  },
}
</script>

<style scoped>
.issuer-settings-page {
  --page-border: rgba(255, 255, 255, 0.1);
  --page-surface: rgba(255, 255, 255, 0.04);
  --page-muted: #a3a3a8;
  width: min(1240px, calc(100% - 40px));
  min-height: calc(100vh - 88px);
  margin: 0 auto;
  padding: 24px 0 40px;
  color: #f5f5f5;
  font-size: 1rem;
}

.issuer-hero,
.settings-shell {
  border: 1px solid var(--page-border);
  background: rgba(22, 22, 23, 0.82);
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.22);
  backdrop-filter: blur(18px);
}

.issuer-hero {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 18px;
  padding: 22px 24px;
  border-radius: 24px;
  background: radial-gradient(circle at 90% 0%, rgba(0, 150, 64, 0.13), transparent 35%), rgba(22, 22, 23, 0.82);
}

.hero-main,
.section-copy,
.issuer-heading,
.footer-hint {
  display: flex;
  align-items: center;
}

.hero-main {
  min-width: 0;
  gap: 14px;
}

.touch-target {
  min-width: 44px !important;
  min-height: 44px !important;
}

.back-button {
  flex: 0 0 auto;
}

.hero-icon {
  display: grid;
  flex: 0 0 48px;
  width: 48px;
  height: 48px;
  place-items: center;
  border: 1px solid rgba(101, 201, 143, 0.26);
  border-radius: 16px;
  background: rgba(0, 150, 64, 0.12);
}

.hero-copy {
  min-width: 0;
}

.hero-eyebrow,
.toolbar-eyebrow {
  color: #65c98f;
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.hero-copy h1,
.settings-toolbar h2,
.section-copy h3,
.empty-state h3 {
  margin: 0;
  color: #f5f5f5;
}

.hero-copy h1 {
  margin-top: 2px;
  font-size: clamp(1.35rem, 3vw, 2rem);
  line-height: 1.15;
}

.hero-copy p,
.settings-toolbar p,
.section-copy p,
.empty-state p {
  color: var(--page-muted);
  font-size: 0.875rem;
  line-height: 1.5;
}

.hero-copy p {
  margin: 5px 0 0;
}

.hero-summary {
  display: flex;
  flex: 0 0 auto;
  min-width: 118px;
  padding: 10px 14px;
  flex-direction: column;
  border: 1px solid rgba(101, 201, 143, 0.2);
  border-radius: 14px;
  background: rgba(0, 150, 64, 0.08);
  text-align: right;
}

.hero-summary strong {
  color: #f5f5f5;
  font-size: 1.25rem;
}

.hero-summary span {
  color: var(--page-muted);
  font-size: 0.75rem;
}

.settings-shell {
  overflow: hidden;
  border-radius: 24px;
}

.settings-toolbar {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 24px;
  padding: 22px 24px;
  border-bottom: 1px solid var(--page-border);
}

.settings-toolbar h2 {
  margin-top: 3px;
  font-size: 1.2rem;
}

.settings-toolbar p {
  max-width: 680px;
  margin: 4px 0 0;
}

.toolbar-controls {
  display: flex;
  flex: 0 1 340px;
  min-width: 260px;
  flex-direction: column;
  gap: 10px;
}

.search-field {
  width: 100%;
}

.filter-switch {
  margin: 0;
  padding: 0 2px;
}

.filter-switch ::v-deep .v-label {
  color: #f5f5f5;
  font-size: 0.875rem;
}

.search-field ::v-deep .v-input__slot,
.category-field ::v-deep .v-input__slot,
.rule-field ::v-deep .v-input__slot {
  border-radius: 13px !important;
  background: rgba(255, 255, 255, 0.035) !important;
}

.settings-alert {
  margin: 20px 24px;
  border-radius: 14px;
}

.retry-button {
  min-height: 44px;
  margin-left: 8px;
  text-transform: none;
}

.empty-state {
  display: flex;
  min-height: 340px;
  padding: 40px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
}

.empty-icon {
  display: grid;
  width: 64px;
  height: 64px;
  margin-bottom: 14px;
  place-items: center;
  border-radius: 20px;
  background: rgba(0, 150, 64, 0.1);
}

.empty-state h3 {
  font-size: 1rem;
}

.empty-state p {
  margin: 5px 0 0;
}

.issuer-panels {
  padding: 14px;
  background: transparent;
}

.issuer-panel {
  margin-bottom: 10px;
  overflow: hidden;
  border: 1px solid var(--page-border);
  border-radius: 17px !important;
  background: var(--page-surface) !important;
}

.issuer-panel::before {
  box-shadow: none !important;
}

.issuer-panel-header {
  min-height: 76px;
  padding: 14px 18px;
}

.issuer-heading {
  min-width: 0;
  gap: 13px;
}

.issuer-avatar {
  display: grid;
  flex: 0 0 42px;
  width: 42px;
  height: 42px;
  place-items: center;
  border-radius: 13px;
  background: rgba(0, 150, 64, 0.13);
  color: #7ad69f;
  font-size: 0.75rem;
  font-weight: 800;
}

.issuer-title-wrap {
  min-width: 0;
  flex: 1;
}

.issuer-title-wrap strong,
.issuer-title-wrap span {
  display: block;
}

.issuer-title-wrap strong {
  overflow: hidden;
  color: #f5f5f5;
  font-size: 1rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.issuer-title-wrap span {
  margin-top: 3px;
  overflow: hidden;
  color: var(--page-muted);
  font-size: 0.875rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.status-chip {
  flex: 0 0 auto;
  margin-right: 8px;
  font-size: 0.75rem !important;
}

.issuer-settings {
  padding: 4px 2px 2px;
}

.default-category-section {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(280px, 420px);
  align-items: center;
  gap: 22px;
  padding: 18px;
  border: 1px solid var(--page-border);
  border-radius: 15px;
  background: rgba(255, 255, 255, 0.025);
}

.section-copy {
  min-width: 0;
  align-items: flex-start;
  gap: 11px;
}

.section-icon {
  display: grid;
  flex: 0 0 34px;
  width: 34px;
  height: 34px;
  place-items: center;
  border-radius: 11px;
  background: rgba(0, 150, 64, 0.11);
}

.section-copy h3 {
  font-size: 1rem;
}

.section-copy p {
  margin: 2px 0 0;
}

.rules-section {
  margin-top: 14px;
}

.rules-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  padding: 4px 4px 12px;
}

.add-rule-button,
.save-button {
  min-height: 44px !important;
  border-radius: 12px !important;
  font-size: 0.875rem !important;
  font-weight: 700;
  letter-spacing: 0;
  text-transform: none;
}

.rules-empty {
  display: flex;
  min-height: 70px;
  align-items: center;
  gap: 10px;
  padding: 16px;
  border: 1px dashed var(--page-border);
  border-radius: 14px;
  color: var(--page-muted);
  font-size: 0.875rem;
}

.rule-card {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 9px;
  padding: 13px;
  border: 1px solid var(--page-border);
  border-radius: 15px;
  background: rgba(0, 0, 0, 0.16);
}

.rule-index {
  display: grid;
  flex: 0 0 32px;
  width: 32px;
  height: 32px;
  place-items: center;
  border-radius: 10px;
  background: rgba(0, 150, 64, 0.13);
  color: #7ad69f;
  font-size: 0.75rem;
  font-weight: 800;
}

.rule-fields {
  display: grid;
  min-width: 0;
  flex: 1;
  grid-template-columns: minmax(190px, 0.8fr) minmax(145px, 0.55fr) minmax(240px, 1fr);
  gap: 10px;
}

.rule-actions {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
}

.issuer-alert {
  margin: 12px 0 0;
  border-radius: 13px;
}

.issuer-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  margin-top: 15px;
  padding-top: 15px;
  border-top: 1px solid var(--page-border);
}

.footer-hint {
  gap: 8px;
  color: var(--page-muted);
  font-size: 0.875rem;
}

.save-button:not(.v-btn--disabled) {
  color: #fff !important;
  background: #009640 !important;
}

.issuer-title-wrap .issuer-usage {
  color: #7ad69f;
  font-weight: 700;
}

.issuer-document-actions,
.issuer-action-buttons,
.document-row-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
}

.issuer-document-actions {
  padding: 8px 4px 18px;
}

.issuer-document-actions strong {
  color: #7ad69f;
  font-size: 1rem;
}

.issuer-document-actions p {
  margin: 3px 0 0;
  color: var(--page-muted);
  font-size: 0.875rem;
}

.issuer-action-buttons {
  justify-content: flex-end;
  gap: 8px;
}

.issuer-action {
  min-height: 44px !important;
  max-width: 100%;
  border-radius: 12px !important;
  font-size: 0.875rem !important;
  font-weight: 700;
  letter-spacing: 0;
  text-transform: none;
}

.issuer-documents-dialog,
.issuer-delete-dialog {
  --page-border: rgba(255, 255, 255, 0.12);
  --page-muted: #b6b6bc;
  background: #1c1c1e !important;
  color: #f5f5f5;
  font-size: 1rem;
}

.documents-dialog-header {
  display: flex;
  flex: 0 0 auto;
  align-items: flex-start;
  gap: 16px;
  padding: 24px;
  border-bottom: 1px solid var(--page-border);
}

.documents-dialog-header > div,
.document-details {
  flex: 1;
  min-width: 0;
}

.documents-dialog-header h2,
.issuer-delete-dialog h2 {
  margin: 4px 0 8px;
  font-size: 1.25rem;
  line-height: 1.4;
  overflow-wrap: anywhere;
}

.documents-dialog-header p {
  margin: 0;
  color: var(--page-muted);
  font-size: 0.875rem;
}

.documents-dialog-body {
  padding: 20px 24px !important;
  color: #f5f5f5 !important;
  font-size: 1rem;
}

.documents-intro {
  color: var(--page-muted);
  font-size: 1rem;
  line-height: 1.6;
}

.documents-empty {
  padding: 36px 12px;
  text-align: center;
}

.documents-empty h3,
.document-details h3 {
  margin: 0 0 12px;
  font-size: 1rem;
  line-height: 1.5;
  overflow-wrap: anywhere;
}

.documents-empty p {
  margin: 12px 0 0;
  font-size: 0.875rem;
  color: var(--page-muted);
}

.document-rows {
  display: grid;
  gap: 12px;
}

.document-row {
  min-width: 0;
  padding: 18px;
  border: 1px solid var(--page-border);
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.025);
}

.document-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 12px 20px;
  margin: 0;
}

.document-meta > div {
  min-width: 0;
  flex: 1 1 8rem;
  overflow-wrap: anywhere;
}

.document-meta dt {
  color: var(--page-muted);
  font-size: 0.75rem;
}

.document-meta dd {
  margin: 3px 0 0;
  color: #f5f5f5;
  font-size: 0.875rem;
}

.document-editor {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: start;
  gap: 12px;
  margin-top: 20px;
}

.document-issuer-field {
  min-width: 0;
  font-size: 1rem;
}

.document-issuer-field ::v-deep .v-select__selection {
  max-width: 100%;
  overflow-wrap: anywhere;
  white-space: normal;
}

.documents-dialog-footer {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 12px 20px;
  border-top: 1px solid var(--page-border);
  font-size: 0.875rem;
}

.issuer-delete-dialog {
  padding: 24px;
}

.issuer-delete-dialog p {
  font-size: 1rem;
  line-height: 1.6;
  overflow-wrap: anywhere;
}

@media (max-width: 959px) {
  .issuer-settings-page {
    width: min(100% - 28px, 1240px);
    padding-top: 16px;
  }

  .settings-toolbar,
  .default-category-section {
    grid-template-columns: 1fr;
    align-items: stretch;
  }

  .settings-toolbar {
    flex-direction: column;
  }

  .toolbar-controls {
    width: 100%;
    min-width: 0;
    flex-basis: auto;
  }

  .rule-card {
    align-items: flex-start;
  }

  .rule-fields {
    grid-template-columns: 1fr 1fr;
  }

  .rule-category-field {
    grid-column: 1 / -1;
  }

  .rule-actions {
    flex-direction: column;
  }

  .document-editor {
    grid-template-columns: minmax(0, 1fr);
  }
}

@media (max-width: 599px) {
  .issuer-settings-page {
    width: calc(100% - 20px);
    padding: 10px 0 24px;
  }

  .issuer-hero {
    align-items: flex-start;
    padding: 16px;
    border-radius: 20px;
  }

  .hero-main {
    align-items: flex-start;
    gap: 9px;
  }

  .hero-icon,
  .hero-copy p,
  .hero-summary,
  .status-chip {
    display: none;
  }

  .settings-shell {
    border-radius: 20px;
  }

  .settings-toolbar {
    gap: 16px;
    padding: 18px;
  }

  .issuer-panels {
    padding: 9px;
  }

  .issuer-panel-header {
    min-height: 70px;
    padding: 12px;
  }

  .issuer-title-wrap span {
    white-space: normal;
  }

  .default-category-section {
    min-width: 0;
    padding: 14px;
  }

  .rules-heading,
  .issuer-footer {
    align-items: stretch;
    flex-direction: column;
  }

  .add-rule-button,
  .save-button {
    width: 100%;
  }

  .rule-card {
    position: relative;
    padding: 14px;
    flex-wrap: wrap;
  }

  .rule-fields {
    order: 2;
    width: 100%;
    flex-basis: 100%;
    grid-template-columns: 1fr;
  }

  .rule-category-field {
    grid-column: auto;
  }

  .rule-actions {
    margin-left: auto;
    flex-direction: row;
  }

  .issuer-document-actions,
  .document-row-heading {
    align-items: stretch;
    flex-direction: column;
  }

  .issuer-document-actions .issuer-action-buttons,
  .issuer-document-actions .issuer-action {
    width: 100%;
  }

  .documents-dialog-header {
    padding: 18px 16px;
    gap: 8px;
  }

  .documents-dialog-body {
    padding: 16px !important;
  }

  .document-row {
    padding: 14px;
  }

  .document-editor .issuer-action {
    flex: 1 1 auto;
  }
}
</style>
